import { FormLabel } from '@/components/FormLabel'
import { FormSection, FormSectionBody, FormSectionDescription, FormSectionHeader, FormSectionTitle } from '@/components/FormSection'
import { FormSpacer } from '@/components/FormSpacer'
import { RadioStack } from '@/components/RadioStack'
import RuianAddressSelector from '@/components/RuianAddressSelector'
import { CustomNumberInput } from '@/containers/CustomNumberInput'
import { useInsurers } from '@/hooks'
import { useNameof } from '@/hooks/useNameof'
import { Box, FormControl, FormErrorMessage, Radio, RadioGroup, useDisclosure } from '@chakra-ui/react'
import { CustomCollapse } from 'BrokerTsLib/src/components'
import type { InsuranceParamsPropertyDto } from 'BrokerTsLib/src/dtos'
import { Insurer, Selection } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { KNButton } from './Step1Form'

export function LocationForm() {
  const nameof = useNameof<InsuranceParamsPropertyDto>()

  const { isInsurerAvailable } = useInsurers(false, null)

  const methods = useFormContext<InsuranceParamsPropertyDto>()
  const {
    register,
    setValue,
    watch,
    getValues,
    formState: { errors }
  } = methods

  const isContracted = !!getValues('generalParams.contractNumber')
  const selectedRealtyType = watch('property.data.realtyTypeId')

  const isGarageAtOtherLocationAvailable = isInsurerAvailable(Insurer.CPP) || isInsurerAvailable(Insurer.Direct)
  const { isOpen: isOpenGarageAtOtherLocation } = useDisclosure({
    isOpen: watch('property.data.hasGarageOnDifferentLocation') == true,
  })

  const isOpenApartment = !!selectedRealtyType && (selectedRealtyType == Selection.Byt || selectedRealtyType == Selection.NajemniByt)

  return (
    <FormSection>
      <FormSectionHeader>
        <FormSectionTitle>Místo pojištění</FormSectionTitle>

        <FormSectionDescription>
          Zadejte základní údaje o nemovitosti.
        </FormSectionDescription>
      </FormSectionHeader>

      <FormSectionBody>
        <KNButton />

        <RuianAddressSelector addressPath={nameof(x => x.property.data.address)} isDisabled={isContracted} />

        <FormSpacer />

        <CustomCollapse in={!!selectedRealtyType && !isOpenApartment}>
          <FormControl isRequired isInvalid={!!errors.property?.data?.hasBasement}>
            <FormLabel>Je dům podsklepený</FormLabel>

            <RadioGroup isDisabled={isContracted} value={watch('property.data.hasBasement') == true ? 'yes' : watch('property.data.hasBasement') == false ? 'no' : undefined} onChange={val => setValue('property.data.hasBasement', val === 'yes')}>
              <RadioStack variant="stackedTile">
                <Radio value="yes">Ano</Radio>
                <Radio value="no">Ne</Radio>
              </RadioStack>
            </RadioGroup>

            {errors.property?.data?.hasBasement && <FormErrorMessage>
              {errors.property?.data?.hasBasement.message}
            </FormErrorMessage>}
          </FormControl>
        </CustomCollapse>

        <CustomCollapse in={isOpenApartment}>
          <FormControl isInvalid={!!errors.property?.data?.hasNonresidentialPremises} isRequired>
            <FormLabel tooltip="Nejčastěji sklepní kóje nebo garáž.">
              Nebytové prostory
            </FormLabel>

            <RadioGroup isDisabled={isContracted} value={watch('property.data.hasNonresidentialPremises') == true ? 'yes' : watch('property.data.hasNonresidentialPremises') == false ? 'no' : undefined} onChange={val => setValue('property.data.hasNonresidentialPremises', val === 'yes')}>
              <RadioStack variant="stackedTile">
                <Radio value="yes">Ano</Radio>
                <Radio value="no">Ne</Radio>
              </RadioStack>
            </RadioGroup>

            {errors.property?.data?.hasNonresidentialPremises && <FormErrorMessage>
              {errors.property?.data?.hasNonresidentialPremises.message}
            </FormErrorMessage>}
          </FormControl>
        </CustomCollapse>

        <CustomCollapse in={isGarageAtOtherLocationAvailable}>
          <FormControl isRequired isInvalid={!!errors.property?.data?.hasGarageOnDifferentLocation}>
            <FormLabel tooltip="Garáž se nachází na jiné adrese než místo pojištění.">
              Garáž na jiné adrese
            </FormLabel>

            <RadioGroup isDisabled={isContracted} value={watch('property.data.hasGarageOnDifferentLocation') == true ? 'yes' : watch('property.data.hasGarageOnDifferentLocation') == false ? 'no' : undefined} onChange={val => setValue('property.data.hasGarageOnDifferentLocation', val === 'yes')}>
              <RadioStack variant="stackedTile">
                <Radio value="yes">Ano</Radio>
                <Radio value="no">Ne</Radio>
              </RadioStack>
            </RadioGroup>

            {errors.property?.data?.hasGarageOnDifferentLocation && <FormErrorMessage>
              {errors.property?.data?.hasGarageOnDifferentLocation.message}
            </FormErrorMessage>}
          </FormControl>
        </CustomCollapse>

        <CustomCollapse in={isOpenGarageAtOtherLocation}>
          <FormSpacer />

          <Box sx={{
            display: 'grid',
            gap: 4
          }}>
            <FormControl isInvalid={!!errors.property?.data?.garageAtOtherLocationAmount} isRequired>
              <FormLabel tooltip="Pojistná částka budovy garáže">
                Pojistná částka
              </FormLabel>

              <CustomNumberInput w={48} defaultValue={getValues('property.data.garageAtOtherLocationAmount')} onValueChange={(_, value) => setValue('property.data.garageAtOtherLocationAmount', value)} isDisabled={isContracted} inputRightElement={'Kč'} inputProps={{
                placeholder: 'Např. 1 500 000'
              }} />

              {errors.property?.data?.garageAtOtherLocationAmount && <FormErrorMessage>
                {errors.property?.data?.garageAtOtherLocationAmount.message}
              </FormErrorMessage>}
            </FormControl>

            <RuianAddressSelector addressPath={nameof(x => x.property.data.garageAddress)} isDisabled={isContracted} />

          </Box>
        </CustomCollapse>
      </FormSectionBody>
    </FormSection>)
}
